import {
  ChangeDetectionStrategy,
  Component,
  effect,
  inject,
  input,
  output,
  signal,
} from "@angular/core";
import { FormControl } from "@angular/forms";

import { SlideOverlayPageService } from "@components/shared/overlay/slide-overlay-page/slide-overlay-page.service";
import { RiskMitigation } from "@components/shared/risk-mitigations/models";
import { TextConstants } from "@shared/constants";

import {
  RiskAssessmentReportIndicatorMitigationStatus,
  RiskAssessmentReportIndicatorStatusEnum,
} from "../../../constants";
import {
  RiskAssessmentReportIndicator,
  RiskAssessmentReportIndicatorMitigation,
} from "../../../models";
import { RiskAssessmentReportIndicatorMitigationsMapService } from "../../../services";

export interface SaveNoteEvent {
  indicatorId: string;
  note: string;
}

export interface RemoveMitigationEvent {
  indicatorId: string;
  mitigationId: string;
}

export interface UpdateMitigationStatusEvent {
  indicatorId: string;
  mitigation: RiskAssessmentReportIndicatorMitigation;
  status: RiskAssessmentReportIndicatorMitigationStatus;
}

export interface IndicatorStatusChangeEvent {
  indicatorId: string;
  status: RiskAssessmentReportIndicatorStatusEnum;
}

@Component({
  selector: "app-risk-assessment-report-overlay-indicator-card",
  templateUrl: "./risk-assessment-report-overlay-indicator-card.component.html",
  styleUrls: ["./risk-assessment-report-overlay-indicator-card.component.scss"],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class RiskAssessmentReportOverlayIndicatorCardComponent {
  readonly overlay = inject(SlideOverlayPageService);

  private readonly mitigationsMapService = inject(
    RiskAssessmentReportIndicatorMitigationsMapService,
  );

  readonly position = input.required<number>();

  readonly noteControl = input.required<FormControl<string>>();

  readonly indicator = input.required<RiskAssessmentReportIndicator["indicators"][0]>();

  readonly mitigations = input.required<RiskAssessmentReportIndicatorMitigation[]>();

  readonly isStatusLoading = input(true);

  readonly saveNote = output<SaveNoteEvent>();

  readonly addMitigation = output<RiskMitigation>();

  readonly removeMitigation = output<RemoveMitigationEvent>();

  readonly updateMitigationStatus = output<UpdateMitigationStatusEvent>();

  readonly resetResponse = output<RiskAssessmentReportIndicator["indicators"][0]>();

  readonly statusChange = output<IndicatorStatusChangeEvent>();

  readonly openMitigationsPanel = output();

  readonly showGuidance = signal(false);

  readonly mitigationsCompleted = signal(false);

  readonly indicatorStatus = RiskAssessmentReportIndicatorStatusEnum;

  readonly statusOptions = [
    {
      value: RiskAssessmentReportIndicatorStatusEnum.RISK_ABSENT,
      label: $localize`:@@label.riskabsent:Risk absent`,
    },
    {
      value: RiskAssessmentReportIndicatorStatusEnum.RISK_PRESENT,
      label: $localize`:@@label.riskpresent:Risk present`,
    },
    {
      value: RiskAssessmentReportIndicatorStatusEnum.NOT_APPLICABLE,
      label: $localize`:@@label.notapplicable:Not applicable`,
    },
    { value: null, label: "" },
  ];

  readonly translations = {
    noteLabel: $localize`:@@shared.note:Note`,
    optionalNotePh: `${$localize`:@@riskassessment.notes.help:Type your note here...`} (${TextConstants.OPTIONAL})`,
    mitigationsLabel: $localize`:@@ui.mitigations:Mitigations`,
    resetResponseTp: $localize`:@@action.ui.resetresponse:Reset response`,
    riskPresentMitigated: TextConstants.RISK_PRESENT_MITIGATED,
  };

  get label() {
    return `${this.statusOptions.find((r) => r.value === this.indicator().status).label} ${this.mitigationsCompleted() ? "(mitigated)" : ""}`.trim();
  }

  constructor() {
    effect(() => this.updateMitigationsCompleted());
  }

  toggleShowGuidance() {
    this.showGuidance.update((show) => !show);
  }

  onSave() {
    this.saveNote.emit({
      indicatorId: this.indicator().id,
      note: this.noteControl().value,
    });
  }

  onStatusChange(status: RiskAssessmentReportIndicatorStatusEnum) {
    this.statusChange.emit({
      indicatorId: this.indicator().id,
      status,
    });

    this.overlay.closeSideMenu();
  }

  private updateMitigationsCompleted() {
    const mitigations: RiskAssessmentReportIndicatorMitigation[] =
      this.mitigationsMapService.map().get(this.indicator().id) || [];

    const noPendingMitigations = mitigations.every((m) => m.status !== null);

    const atLeastOneCompletedMitigation = mitigations.some(
      (m) => m.status === RiskAssessmentReportIndicatorMitigationStatus.COMPLETED,
    );

    const allCompleted = noPendingMitigations && atLeastOneCompletedMitigation;

    this.mitigationsCompleted.set(allCompleted);
  }
}
